import { useEffect, useState, useCallback, useRef } from "react";
import {
  FacebookPublish,
  YoutubePublish,
  SocialChannel,
  SocialPublish,
  GenericSocialPublish,
} from "@/database";
import { CustomSelect, toast, Tooltip, Button } from "../..";
import {
  socialPublishStatusToTextAndColor,
  formatTimestampToAgo,
  openVideoInNewTab,
} from "@/utils";
import { getPlatformIcon } from "@/utils/utilFunctions/commmonFunctions";
import EditIcon from "../../../assets/icons/svg/edit.svg?react";
import noImage from "../../../assets/icons/svg/no-image.svg";
import ViewIcon from "../../../assets/icons/svg/visibility.svg?react";
import { Offcanvas } from "react-bootstrap";
import { RootState } from "@/store/index";
import YoutubePublishCard from "./YoutubePublishCard";
import {
  callPublishMedia,
  updatePublishMedia,
} from "@/store/media/media.action";
import { connect, ConnectedProps } from "react-redux";
import { useMemo } from "react";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { getMostFrequentNoIndex } from "@/store/user/user.action";
import { getYoutubePlaylist, getSocialChannels } from "@/store/app/app.actions";
import SocialPublishCard from "./SocialPublishCard";
import AlertDialog from "@/utils/ui/AlertDialog/AlertDialog";
import {
  deleteSocialPublish,
  takedownSocialPublish,
} from "@/store/app/app.actions";
import { updateModal } from "@/store/app/app.slice";
import { addMediaAsset } from "@/store/media/media.slice";
import { useSearchParams } from "react-router-dom";
import { PresetListModel, SocialPresetModel } from "@/database";
import { usePublishManagement } from "./hooks/usePublishManagement";
import { useCardInteraction } from "./hooks/useCardInteraction";
import { useChannelOptions } from "./hooks/useChannelOptions";
import { usePresetManagement } from "./hooks/usePresetManagement";
import type { AutoYoutubeChannel } from "@/components/MediaPublish/PlaylistPublish/PlaylistPublish";
import { PLATFORM_PUBLISH_CONFIG, getPlatformConfig } from "./platformConfig";
import {
  SOCIAL_PUBLISH_STATUS,
  DEFAULT_YOUTUBE_CATEGORIES,
  PUBLISH_RANGE_TYPE,
} from "@/constants";
import SocialExpandSheet, {
  type ExpandDescriptor,
  plainItemId,
  presetItemId,
} from "./SocialExpandSheet";
import {
  synthesizeYoutubeChannel,
  isPendingPublish,
  isDraftPublish,
  isPreActivationPublish,
  isOverridablePublish,
  statusTooltip,
  buildDefaultPublishPayload,
  validatePublishPayload,
  editedFieldsToArray,
} from "./utils";
import { resolveThumbIdByOrientation } from "@/utils/mediautils/CommonFunctions";

export interface SocialGuard {
  hasPendingPublish: () => boolean;
  pendingCount: () => number;
  promptPublish: () => void;
  publishStaged: (opts?: { mediaWillBeActive?: boolean }) => void;
  validateStaged: (opts?: { mediaWillBeActive?: boolean }) => string | null;
}

export interface PresetPlatformControl {
  removeChannel: (channelId: string) => void;
  openChannel: (channelId: string) => void;
}

interface IProps {
  isWizard?: boolean;
  isPreset?: boolean;
  handleAllSocialPresets?: (presets: any[]) => void;
  item?: PresetListModel;
  presetDetails?: SocialPresetModel[];
  isPreview?: boolean;
  readOnlyMode?: boolean;
  presetList?: PresetListModel[];
  metaPresetData?: any;
  embedded?: boolean;
  autoYoutubeChannels?: AutoYoutubeChannel[];
  socialGuardRef?: React.MutableRefObject<SocialGuard | null>;
  onYoutubeSelectionChange?: (channelNames: string[]) => void;
  onDirty?: () => void;
  /**
   * Two-way sync with PresetCardList's per-platform toggles: this ref lets the
   * list-side toggle remove a destination that's already open in the sheet,
   * and `onPresetPlatformRemoved` reports back sheet-side (× button) removals
   * so the list can uncheck that platform's toggle.
   */
  presetPlatformRef?: React.MutableRefObject<PresetPlatformControl | null>;
  onPresetPlatformRemoved?: (channelId: string) => void;
  /**
   * The sheet was dismissed without saving (outside/backdrop click, Escape,
   * or "Close without saving?" confirm) - reports every destination it was
   * showing so the list can turn that whole preset's switch off, not just
   * uncheck the one channel that happened to be open.
   */
  onSheetDismissed?: (channelIds: string[]) => void;
  /**
   * Live (unsaved) title/desc/tags/thumbnail from the Media Detail form. Redux
   * `currentMediaDetails` only reflects the last-saved media, so without this the
   * social cards never see in-progress edits. When provided, these values drive
   * `buildPublish` and keep draft cards' synced fields fresh.
   */
  liveMedia?: {
    title?: string;
    desc?: string;
    tags?: string[];
    thumb?: string;
    thumb_id?: string;
  };
}

const AllSocialPublish = ({
  currentMediaDetails,
  assets,
  callPublishMedia,
  updatePublishMedia,
  isPublishingMedia,
  socialChannels,
  getMostFrequentNoIndex,
  mostFrequentSocial,
  getYoutubePlaylist,
  youtubePlaylist,
  deleteSocialPublish,
  takedownSocialPublish,
  updateModal,
  isWizard = false,
  mediaAssets,
  addMediaAsset,
  isPreset = false,
  handleAllSocialPresets,
  item,
  presetDetails,
  isPreview = false,
  readOnlyMode = false,
  metaPresetData,
  bunchLanguageList,
  embedded = false,
  autoYoutubeChannels = [],
  socialGuardRef,
  onYoutubeSelectionChange,
  onDirty,
  liveMedia,
  getSocialChannels,
  presetPlatformRef,
  onPresetPlatformRemoved,
  onSheetDismissed,
}: Props) => {
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  // Embedded mode: the social card opens in a right-side Offcanvas sheet keyed by channel id.
  const [sheetCardId, setSheetCardId] = useState<string | null>(null);
  const [invalidPublishIds, setInvalidPublishIds] = useState<Set<string>>(
    new Set()
  );
  // Non-embedded page: a destination added via a "Recently Used" chip opens
  // straight into the sheet and must not show as a page card/accordion until
  // the user actually publishes/saves it from the sheet. Cleared once the
  // real submit happens (handlePublished) or the destination is removed.
  const [sheetOnlyPendingIds, setSheetOnlyPendingIds] = useState<Set<string>>(
    new Set()
  );
  // Non-embedded page: when a preset adds multiple destinations at once, they
  // all open in the same sheet as tabs instead of separate inline page cards.
  const [presetSheetGroupIds, setPresetSheetGroupIds] = useState<string[]>([]);
  // Destinations with edits made in the sheet that haven't been Saved/Published
  // yet - drives the tab's unsaved-changes dot and the close confirmation.
  const [unsavedTabIds, setUnsavedTabIds] = useState<Set<string>>(new Set());
  const [sheetCloseConfirmOpen, setSheetCloseConfirmOpen] = useState(false);
  // Embedded mode: the expanded destination/preset browser (mirrors the playlist ExpandSheet).
  const [expandOpen, setExpandOpen] = useState(false);
  const [expandBtnHovered, setExpandBtnHovered] = useState(false);
  // Auto (playlist-driven) chips the user removed via the chip's ×; suppressed from re-adding
  // until the source playlist is deselected (mirrors the draft-chip dismissal in Denmark).
  const [dismissedAutoIds, setDismissedAutoIds] = useState<Set<string>>(
    new Set()
  );
  const [searchParams] = useSearchParams();
  const hasProcessedPresets = useRef(false);
  const liveMediaRef = useRef(liveMedia);
  liveMediaRef.current = liveMedia;

  // Custom hooks
  const {
    youtubePublishes,
    facebookPublishes,
    socialPublishes,
    publishOrder,
    addPublish,
    removePublish,
    setYoutubePublishes,
    setFacebookPublishes,
    setSocialPublishes,
  } = usePublishManagement({
    currentMediaDetails,
    isPreview,
  });

  // Prune sheet-only-pending ids once they're removed (cancel/delete) so the
  // set never leaks stale ids for destinations that no longer exist.
  useEffect(() => {
    setSheetOnlyPendingIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((id) => publishOrder.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [publishOrder]);

  const {
    expandedCardIds,
    activeCardId,
    setActiveCardId,
    handleCardInteraction,
    markCardExpanded,
    collapseCard,
    scrollToCard,
    setCardRef,
    publishRefs,
    resetInteraction,
  } = useCardInteraction();

  // When the active media changes (e.g. switching bulk-upload tabs), close any
  // open/expanded social chip and drop its unsaved draft state instead of
  // carrying it over onto the newly active media.
  const prevMediaIdForResetRef = useRef(currentMediaDetails?.id);
  useEffect(() => {
    if (prevMediaIdForResetRef.current === currentMediaDetails?.id) return;
    prevMediaIdForResetRef.current = currentMediaDetails?.id;

    setSheetCardId(null);
    setExpandOpen(false);
    setSelectedChannel("");
    setInvalidPublishIds(new Set());
    setDismissedAutoIds(new Set());
    resetInteraction();
    hasProcessedPresets.current = false;
  }, [currentMediaDetails?.id, resetInteraction]);

  // Pending "cancel" (remove chip) awaiting user confirmation in the AlertDialog.
  const [cancelConfirm, setCancelConfirm] = useState<{
    initialdata: any;
    cardType: string;
  } | null>(null);

  // Deletes of already-published chips are removed from the UI immediately but the
  // actual delete API call is deferred until the media form is saved (Save / Save &
  // Publish / Save & Update) — flushed via the guard's publishStaged().
  const pendingDeletesRef = useRef<{ id: string; type: string }[]>([]);

  const { channelOptions, mfuChannelOptions } = useChannelOptions({
    socialChannels,
    mostFrequentSocial,
  });
  // A channel with an unsubmitted draft chip (isNew === 100) must stay excluded —
  // reselecting it should reopen that draft, not add a second blank one. An
  // already-published/queued/failed channel stays selectable: picking it again
  // always starts a fresh republish attempt (handleChannelChange's
  // findInProgressEntry only matches a still-open local draft, never a live
  // record — see its comment above).
  const draftChannelIds = useMemo(
    () =>
      new Set(
        [...youtubePublishes, ...facebookPublishes, ...socialPublishes]
          .filter((p: any) => isDraftPublish(p))
          .map((p: any) => p.id)
      ),
    [youtubePublishes, facebookPublishes, socialPublishes]
  );
  const availableChannelOptions = useMemo(
    () => channelOptions.filter((o) => !draftChannelIds.has(String(o.value))),
    [channelOptions, draftChannelIds]
  );

  const { handleAllSelectedPresets, setAllSelectedPresets } =
    usePresetManagement({
      item,
      presetDetails,
      handleAllSocialPresets,
    });

  useEffect(() => {
    if (!currentMediaDetails?.images?.list?.[0].name) return;
    if (currentMediaDetails?.images?.list?.[0].name) {
      const newAsset = {
        baseurl: generateBaseUrl(currentMediaDetails),
        ct: currentMediaDetails?.ct,
        id: currentMediaDetails?.images?.list?.[0].name,
        mt: currentMediaDetails?.mt,
        name: "thumb",
        nid: 0,
        pid: currentMediaDetails?.id,
        status: 0,
        type: "thumb",
        user: "",
      };
      if (mediaAssets.length > 0) {
        if (!mediaAssets.find((asset) => asset.id === newAsset.id)) {
          addMediaAsset({ asset: newAsset });
        }
      } else {
        addMediaAsset({ asset: newAsset });
      }
    }
  }, [currentMediaDetails, mediaAssets, addMediaAsset]);

  useEffect(() => {
    getMostFrequentNoIndex({
      field: "value",
      size: 1000,
      key: "social",
      mfu: true,
    });
  }, [getMostFrequentNoIndex]);

  // When embedded on the media edit page, the channel list isn't preloaded (the route loads it on
  // tab switch), so fetch it here if missing.
  useEffect(() => {
    if (embedded && !socialChannels?.socialChannelsList?.length) {
      getSocialChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  // A not-yet-submitted local draft chip uses the real channel id as its own
  // card id (see buildPublish/handleChannelChange below) - once it's actually
  // submitted, the synced-from-ES card gets its own stable record id instead
  // (see usePublishManagement), so an `id === channel id` match here can only
  // ever be that still-open draft. Reselecting the channel should reopen it
  // rather than spawn a second one; reselecting a channel with only live
  // records (no match here) always starts a fresh republish attempt.
  const findInProgressEntry = useCallback(
    (id: string): any | null => {
      const entry = (
        [...youtubePublishes, ...facebookPublishes, ...socialPublishes] as any[]
      ).find((publish) => publish.id === id);
      if (!entry) return null;
      return isPreActivationPublish(entry) || isPendingPublish(entry)
        ? entry
        : null;
    },
    [youtubePublishes, facebookPublishes, socialPublishes]
  );

  const scrollToExistingPublish = useCallback(
    (id: string): boolean => {
      const existingIndexInYt = youtubePublishes.findIndex(
        (publish) => publish.id === id
      );
      const existingIndexInFb = facebookPublishes.findIndex(
        (publish) => publish.id === id
      );
      const existingIndexInOther = socialPublishes.findIndex(
        (publish) => publish.id === id
      );

      // Whether the channel already has a publish entry must be judged from
      // state alone - if the card's DOM ref isn't attached yet (e.g. right
      // after mount, before this render's refs commit), the caller would
      // otherwise wrongly conclude none exists and add a duplicate.
      const exists =
        existingIndexInYt >= 0 ||
        existingIndexInFb >= 0 ||
        existingIndexInOther >= 0;
      if (!exists) return false;

      const scrollTargetId =
        existingIndexInYt >= 0
          ? youtubePublishes[existingIndexInYt].id
          : existingIndexInFb >= 0
            ? facebookPublishes[existingIndexInFb].id
            : socialPublishes[existingIndexInOther].id;

      const targetElement = publishRefs.current.get(scrollTargetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }

      return true;
    },
    [youtubePublishes, facebookPublishes, socialPublishes, publishRefs]
  );

  // Build a publish-entry object for a social channel + the current media. Shared by the manual
  // channel-add flow and the embedded auto-add-from-playlist flow. `extra` overrides specific
  // fields (e.g. a synthetic id + autoAdded marker for playlist-driven YouTube chips).
  const buildPublish = useCallback(
    (channelDetails: SocialChannel, extra: Record<string, any> = {}) => {
      if (!channelDetails) return null;
      const platformConfig =
        PLATFORM_PUBLISH_CONFIG[
          channelDetails.platform as keyof typeof PLATFORM_PUBLISH_CONFIG
        ];
      if (!platformConfig) return null;

      const { platform, id, name, desc, tags, image, title } = channelDetails;
      const titlePrefix = (channelDetails as any)?.title_prefix || "";
      const titleSuffix = (channelDetails as any)?.title_suffix || "";
      const descPrefix = (channelDetails as any)?.description_prefix || "";
      const descSuffix = (channelDetails as any)?.description_suffix || "";
      const {
        desc: savedDesc,
        tags: savedTags,
        title: savedTitle,
        id: mediaId,
      } = currentMediaDetails;

      // Prefer live (unsaved) form values over the last-saved redux media.
      const mediaTitle =
        liveMedia?.title != null ? liveMedia.title : savedTitle;
      const mediaDesc = liveMedia?.desc != null ? liveMedia.desc : savedDesc;
      const mediaTags = liveMedia?.tags != null ? liveMedia.tags : savedTags;

      const baseUrl =
        assets?.find((a) => a?.name === "thumb")?.baseurl ||
        assets?.[0]?.baseurl ||
        generateBaseUrl(currentMediaDetails) ||
        "";
      const tempThumbId =
        liveMedia?.thumb_id || resolveThumbIdByOrientation(currentMediaDetails);

      const thumbUrl = liveMedia?.thumb
        ? liveMedia.thumb
        : baseUrl
          ? `${baseUrl}/${tempThumbId}.jpg` // converts to 16/9 ratio
          : getThumbnailUrlUtil({
              media: currentMediaDetails,
              id: tempThumbId,
              source: currentMediaDetails?.source,
            });

      return {
        id,
        channel_name: name,
        platform,
        image,
        tags: mediaTags || [],
        thumb: thumbUrl,
        thumb_id: tempThumbId,
        title: mediaTitle,
        desc: mediaDesc,
        status: SOCIAL_PUBLISH_STATUS.QUEUE,
        isNew: 100,
        media_id: mediaId,
        ct: 0,
        mt: 0,
        routing_id: id,
        social_title: title,
        social_desc: desc,
        social_tags: tags || [],
        title_prefix: titlePrefix,
        title_suffix: titleSuffix,
        description_prefix: descPrefix,
        description_suffix: descSuffix,
        job_type: platformConfig.job_type,
        upload_type: platformConfig.upload_type,
        ...platformConfig.getAdditionalProps(channelDetails),
        ...extra,
      } as any; // dynamic platform-specific properties, extra's own
      // title/desc/tags (e.g. a borrowed preset social's saved content)
      // intentionally win over the media-derived defaults above.
    },
    [currentMediaDetails, assets, liveMedia]
  );

  // Keep already-added DRAFT cards (isNew === 100, not yet published) in sync with
  // live Media Detail edits. Only patch the media-sourced fields (title/desc/tags/
  // thumb); the card itself decides per-field whether to accept the update (it
  // ignores fields the user has edited on the card).
  useEffect(() => {
    if (!liveMedia) return;

    const patch = (list: any[]) => {
      let listChanged = false;
      const next = list.map((p) => {
        if (!isPreActivationPublish(p)) return p;
        const editedFields = p?._editedFields || {};
        const cardSaved = !!p?._cardSaved;
        const canSync = (field: string) => !cardSaved && !editedFields[field];
        let itemChanged = false;
        const updated = { ...p };
        if (
          canSync("title") &&
          liveMedia.title != null &&
          p.title !== liveMedia.title
        ) {
          updated.title = liveMedia.title;
          itemChanged = true;
        }
        if (
          canSync("desc") &&
          liveMedia.desc != null &&
          p.desc !== liveMedia.desc
        ) {
          updated.desc = liveMedia.desc;
          itemChanged = true;
        }
        if (
          canSync("tags") &&
          liveMedia.tags != null &&
          JSON.stringify(p.tags) !== JSON.stringify(liveMedia.tags)
        ) {
          updated.tags = liveMedia.tags;
          itemChanged = true;
        }
        if (
          canSync("thumbnail") &&
          liveMedia.thumb != null &&
          p.thumb !== liveMedia.thumb
        ) {
          updated.thumb = liveMedia.thumb;
          updated.thumb_id = liveMedia.thumb_id;
          itemChanged = true;
        }
        if (itemChanged) {
          listChanged = true;
          return updated;
        }
        return p;
      });
      return listChanged ? next : list;
    };
    setYoutubePublishes((prev) => patch(prev));
    setFacebookPublishes((prev) => patch(prev));
    setSocialPublishes((prev) => patch(prev));
  }, [
    liveMedia,
    setYoutubePublishes,
    setFacebookPublishes,
    setSocialPublishes,
  ]);

  const handleChannelChange = useCallback(
    (
      newValue:
        | {
            value: string;
            isFromPreset?: boolean;
            presetSocialData?: any;
            openSheetOnAdd?: boolean;
          }
        | any
    ) => {
      const selectedChannel = newValue.value;
      const isFromPreset = !!newValue.isFromPreset;
      const presetSocialData = newValue.presetSocialData;
      const openSheetOnAdd = !!newValue.openSheetOnAdd;
      setActiveCardId(selectedChannel);

      // An in-progress entry (draft/queued/failed) for this exact channel id
      // is the *same* attempt still being worked on - reopen it rather than
      // starting another one. A channel whose only entry is already live
      // (published) falls through below to start a fresh republish attempt.
      const inProgressEntry = findInProgressEntry(selectedChannel);
      if (inProgressEntry) {
        // Only an already-used/published destination pops open the side
        // sheet on direct selection. A "Reuse Meta" preset selection
        // shouldn't pop it open by itself either.
        if (!isFromPreset) {
          setSheetCardId(selectedChannel);
        }
        // Recently Used chips open only the side sheet — the inline page
        // card should stay collapsed, not expand in place too.
        if (!openSheetOnAdd && !expandedCardIds.has(selectedChannel)) {
          handleCardInteraction(selectedChannel);
        }
        scrollToExistingPublish(selectedChannel);
        return selectedChannel;
      }

      const channelDetails: SocialChannel =
        socialChannels.socialChannelsList.find(
          (channel: SocialChannel) => channel.id === selectedChannel
        );

      if (!channelDetails) return null;

      setSelectedChannel(selectedChannel);

      // A "Reuse Meta" -> select media borrowed social already carries its
      // own authored title/desc/tags/category/monetization/etc - seed the
      // card with them (instead of the plain media meta buildPublish
      // defaults to, or the channel's own defaults from getAdditionalProps)
      // and mark title/desc/tags pre-edited so the live-media-sync effect
      // doesn't overwrite them. Only copy keys the social actually has -
      // spreading `undefined` for an absent key would blank out a real
      // channel-level default (e.g. YouTube's category/monetize_policy_id).
      const PRESET_SOCIAL_KEYS = [
        "privacy",
        "category",
        "playlist",
        "monetize_policy_id",
        "monetize_policy_name",
        "is_created_for_kids",
        "is_rights_managements_allowed",
      ] as const;
      // A publish-preset's channel_configs entry (applied from the MediaPublish
      // page's PRESET picker) only carries title_prepend/title_append/etc - no
      // real title/desc - and must fall through to buildPublish's media-derived
      // title/desc so BasePublishCard's prepend+mediaTitle+append composition
      // effect can run. A "Reuse Meta" borrowed social (mediaSocialsToPresetSocialPayload)
      // DOES carry the source media's own already-published title/desc, which
      // should win as-is. Only treat title/desc as pre-authored (and lock them
      // from re-composition) when the source actually has real content.
      const hasAuthoredTitle =
        typeof presetSocialData?.title === "string" &&
        presetSocialData.title.trim() !== "";
      const hasAuthoredDesc =
        typeof presetSocialData?.desc === "string" &&
        presetSocialData.desc.trim() !== "";
      const presetExtra = presetSocialData
        ? {
            ...(hasAuthoredTitle ? { title: presetSocialData.title } : {}),
            ...(hasAuthoredDesc ? { desc: presetSocialData.desc } : {}),
            tags: Array.isArray(presetSocialData.tags)
              ? presetSocialData.tags
              : [],
            ...PRESET_SOCIAL_KEYS.reduce(
              (acc, key) => {
                if (presetSocialData[key] != null) {
                  acc[key] = presetSocialData[key];
                }
                return acc;
              },
              {} as Record<string, any>
            ),
            _editedFields: {
              ...(hasAuthoredTitle ? { title: true } : {}),
              ...(hasAuthoredDesc ? { desc: true } : {}),
              tags: true,
            },
          }
        : {};

      // A channel's very first-ever real publish record keeps `id === channel
      // id` (see the `_channelId` doc on SocialPublish in database/media/model.ts) -
      // it never needed disambiguating from itself. If such a record already
      // lives in currentMediaDetails.socials for this channel, a fresh local
      // draft can NOT also use the bare channel id as its own card id: both
      // would collide under that same key in publishLookup (SocialPublish.tsx's
      // youtubePublishes/facebookPublishes/socialPublishes -> Map keyed by id),
      // and since addPublish prepends the draft while the real record sits
      // later in the same array, the later `lookup.set` for the real record
      // silently wins - the tab then resolves to that already-published
      // record instead of the fresh draft, rendering as a locked edit of it
      // (mode picker disabled, one clip pre-selected, others unselectable).
      // Give the draft a synthetic id in that case and carry the real
      // channel id via `_channelId` instead, exactly like a repeat republish
      // attempt already needs to (see the model.ts doc comment on
      // `_channelId`) - a channel with no live record yet still uses the
      // plain channel id, unchanged from before.
      const hasLiveRecordForChannel = (currentMediaDetails?.socials || []).some(
        (s: any) => (s.channel_id || s.id) === selectedChannel
      );
      const draftId = hasLiveRecordForChannel
        ? `${selectedChannel}:draft:${Date.now()}`
        : selectedChannel;
      const buildExtra = {
        ...presetExtra,
        id: draftId,
        _channelId: selectedChannel,
      };

      const newPublish = buildPublish(channelDetails, buildExtra);
      if (!newPublish) return null;

      addPublish(newPublish, channelDetails.platform);
      if (!isFromPreset) {
        onDirty?.();
      }
      // Recently Used chips are an explicit user pick of a familiar
      // destination — open only the same side sheet used everywhere else;
      // skip expanding/scrolling to the inline page card underneath, and
      // don't let it render as a page card until actually published/saved.
      if (openSheetOnAdd) {
        setSheetCardId(newPublish.id);
        setSheetOnlyPendingIds((prev) => new Set(prev).add(newPublish.id));
      } else {
        handleCardInteraction(newPublish.id);
        scrollToCard(newPublish.id);
      }
      return newPublish.id;
    },
    [
      socialChannels,
      buildPublish,
      addPublish,
      scrollToExistingPublish,
      findInProgressEntry,
      currentMediaDetails?.socials,
      expandedCardIds,
      handleCardInteraction,
      scrollToCard,
      setActiveCardId,
      onDirty,
    ]
  );

  // Drop a removed destination from any open preset tab group, hop to a
  // remaining tab (or close the sheet if it was the last one), and report it
  // upstream so PresetCardList's toggle unchecks — shared by every removal
  // path (sheet tab ×, in-card Cancel, in-card Delete, list-side toggle).
  const notifyDestinationRemoved = useCallback(
    (id: string) => {
      // Sole owner of what happens to sheetCardId when the removed
      // destination was the active tab/sheet: hop to a remaining sibling tab
      // if this was part of a preset group, otherwise close the sheet. Any
      // caller that also unconditionally closes the sheet itself (e.g. an
      // older `if (sheetCardId === id) setSheetCardId(null)`) would race this
      // hop in the same batch and can override it depending on React's
      // update ordering - callers must leave that decision to this function.
      const wasInGroup = presetSheetGroupIdsRef.current.includes(id);
      if (wasInGroup) {
        const remaining = presetSheetGroupIdsRef.current.filter(
          (tabId) => tabId !== id
        );
        setPresetSheetGroupIds(remaining);
        setSheetCardId((prevSheetId) =>
          prevSheetId === id
            ? remaining.length > 0
              ? remaining[0]
              : null
            : prevSheetId
        );
      } else {
        setSheetCardId((prevSheetId) =>
          prevSheetId === id ? null : prevSheetId
        );
      }
      onPresetPlatformRemoved?.(id);
    },
    [onPresetPlatformRemoved]
  );

  const performCancel = useCallback(
    (initialdata: any, cardType: string) => {
      setAllSelectedPresets((prev) =>
        prev.filter(
          (preset) =>
            preset?.job_type !== initialdata?.job_type ||
            preset?.channel_name !== initialdata?.channel_name
        )
      );
      removePublish(initialdata?.id, cardType);
      collapseCard(initialdata?.id);
      // notifyDestinationRemoved owns sheetCardId here - see handleRemoveChip
      // for why closing it first races the sibling-tab hop.
      notifyDestinationRemoved(initialdata?.id);
      onDirty?.();
    },
    [
      setAllSelectedPresets,
      removePublish,
      collapseCard,
      onDirty,
      notifyDestinationRemoved,
    ]
  );

  const handleCancelOrDelete = useCallback(
    async (initialdata: any, type: string, cardType: string) => {
      if (type === "cancel") {
        // Confirm before removing the chip and closing its expanded view.
        setCancelConfirm({ initialdata, cardType });
        return;
      }
      setAllSelectedPresets((prev) =>
        prev.filter(
          (preset) =>
            preset?.job_type !== initialdata?.job_type ||
            preset?.channel_name !== initialdata?.channel_name
        )
      );
      if (type === "delete") {
        const isPublished =
          initialdata?.status === SOCIAL_PUBLISH_STATUS.COMPLETED;
        // The sheet must stay open (showing a resolving state on the Delete
        // button - see BasePublishCard) until this RPC actually resolves,
        // not close the instant the button is clicked - a failed
        // takedown/delete should leave the destination exactly as-is rather
        // than closing on a call that never succeeded. The embedded flow's
        // deferred queue (pendingDeletesRef, flushed later by a "Save &
        // Publish" staged batch) has no RPC to await here at all, so it
        // keeps closing immediately, same as before.
        const queueOrDelete = async (id: string, publishType: string) => {
          if (isPublished) {
            // Published media is taken down on the platform; the socket's
            // media.social handler updates redux once the backend confirms.
            await takedownSocialPublish({ doc_id: id, hard_delete: true });
            return;
          }
          if (embedded) {
            pendingDeletesRef.current.push({ id, type: publishType });
          } else if (id) {
            await deleteSocialPublish({ id, type: publishType });
          }
        };
        if (cardType === "youtube") {
          const publish = youtubePublishes.find(
            (p) => p?.id === initialdata?.id
          );
          if (publish) {
            await queueOrDelete(publish.published_id, "youtube");
            if (!isPublished) removePublish(initialdata?.id, cardType);
          }
        } else if (cardType === "facebook") {
          const publish = facebookPublishes.find(
            (p) => p.id === initialdata?.id
          );
          if (publish) {
            await queueOrDelete(publish.published_id, "facebook");
            if (!isPublished) removePublish(initialdata?.id, cardType);
          }
        } else {
          const publish = socialPublishes.find(
            (p) => p?.id === initialdata?.id
          );
          if (publish) {
            await queueOrDelete(publish.published_id, publish.job_type);
            if (!isPublished) removePublish(initialdata?.id, cardType);
          }
        }
        // Collapse and close the card's expanded view — otherwise the sheet stays
        // open showing a blank card for the now-removed chip.
        collapseCard(initialdata?.id);
        // notifyDestinationRemoved owns sheetCardId here - see handleRemoveChip
        // for why closing it first races the sibling-tab hop.
        notifyDestinationRemoved(initialdata?.id);
        onDirty?.();
      }
    },
    [
      setAllSelectedPresets,
      youtubePublishes,
      facebookPublishes,
      collapseCard,
      socialPublishes,
      removePublish,
      onDirty,
      embedded,
      deleteSocialPublish,
      takedownSocialPublish,
      notifyDestinationRemoved,
    ]
  );

  useEffect(() => {
    const notificationId = searchParams.get("notificationId");
    if (notificationId && currentMediaDetails?.socials) {
      const matchingSocial = currentMediaDetails.socials.find(
        (social: any) =>
          social.id === notificationId || social.published_id === notificationId
      );
      if (matchingSocial) {
        const cardId = matchingSocial.platform;
        setActiveCardId(cardId);
        handleCardInteraction(cardId);
      }
    }
  }, [
    searchParams,
    currentMediaDetails?.socials,
    handleCardInteraction,
    setActiveCardId,
  ]);

  // Auto-open card based on item prop
  useEffect(() => {
    // return;
    // if (isPreview) return;

    // Prevent infinite loop by only running once per presetDetails
    if (hasProcessedPresets.current) return;

    if (
      presetDetails &&
      presetDetails.length > 0 &&
      socialChannels.socialChannelsList.length > 0
    ) {
      hasProcessedPresets.current = true;
      const channelsToOpen: string[] = [];
      // Keep the source social item alongside its matched channel id, so its
      // own title/desc/tags can seed the card instead of the plain media meta.
      const channelSocialData = new Map<string, any>();

      // Process each social item in the array
      presetDetails.forEach((socialItem: any) => {
        // `socialItem.platform` is the preset's channel_id (see
        // convertChannelConfigToSocial: `platform: channel_id`) - fall back to
        // matching on it directly when name+job_type doesn't line up (e.g.
        // Facebook page names can drift from what's frozen into the preset),
        // same fallback chain SocialExpandSheet's presetPairs() already uses.
        const matchingChannel = socialChannels.socialChannelsList.find(
          (channel: SocialChannel) =>
            (channel.name === socialItem.channel_name &&
              channel.platform === socialItem.job_type) ||
            channel.id === socialItem.platform ||
            (channel as any).channel === socialItem.platform
        );

        if (matchingChannel) {
          const channelId = matchingChannel.id;
          channelsToOpen.push(channelId);
          channelSocialData.set(channelId, socialItem);
        }
      });

      // Preset-added destinations always open in the shared side sheet — never
      // as inline page cards. Multiple destinations from one preset share the
      // sheet as tabs (presetSheetGroupIds) instead of each getting its own card.
      if (channelsToOpen.length > 0) {
        setActiveCardId(channelsToOpen[0]); // Set first one as active

        // Open/create cards for all channels - handleChannelChange itself
        // decides whether to reopen an in-progress entry or start a new
        // republish attempt on a channel that's already fully published, so
        // it's always the right call here (no need to pre-check existence).
        // It returns the id the card actually ended up under - a repeat
        // attempt gets a synthetic id, not the real channel id - so the
        // sheet/tabs below must be keyed off that, not channelsToOpen itself,
        // or they'd resolve back to the old published card.
        const openedIds = channelsToOpen
          .map((channelId) =>
            handleChannelChange({
              value: channelId,
              isFromPreset: true,
              presetSocialData: channelSocialData.get(channelId),
            })
          )
          .filter((id): id is string => !!id);

        setSheetOnlyPendingIds((prev) => {
          const next = new Set(prev);
          openedIds.forEach((id) => next.add(id));
          return next;
        });
        setPresetSheetGroupIds(openedIds);
        setSheetCardId(openedIds[0] ?? null);
      }
    }
  }, [
    socialChannels.socialChannelsList,
    presetDetails,
    expandedCardIds,
    handleCardInteraction,
    scrollToCard,
    setActiveCardId,
    scrollToExistingPublish,
    handleChannelChange,
  ]);

  // Reset hasProcessedPresets when presetDetails changes (switching between presets)
  useEffect(() => {
    hasProcessedPresets.current = false;
  }, [presetDetails]);

  // Create lookup Map for O(1) access instead of O(n) .find() operations in render loop
  const publishLookup = useMemo(() => {
    const lookup = new Map<
      string,
      { type: "youtube" | "facebook" | "social"; data: any }
    >();
    youtubePublishes.forEach((p) =>
      lookup.set(p.id, { type: "youtube", data: p })
    );
    facebookPublishes.forEach((p) =>
      lookup.set(p.id, { type: "facebook", data: p })
    );
    socialPublishes.forEach((p) =>
      lookup.set(p.id, { type: "social", data: p })
    );
    return lookup;
  }, [youtubePublishes, facebookPublishes, socialPublishes]);

  // A preset tab's id starts as a local draft's own channel id. Once that
  // destination's create RPC actually lands in currentMediaDetails.socials,
  // usePublishManagement's sync effect drops the draft entry (a different,
  // client-generated id) in favor of the real record (keyed by its own
  // backend id, carrying the channel id separately as _channelId) - so the
  // draft id the sheet/tab still points at stops resolving in publishLookup
  // at all, orphaning the tab (blank sheet body) even though the
  // destination itself is still very much there under its new id. Remap
  // presetSheetGroupIds (and sheetCardId, but ONLY while it's pointing at a
  // preset tab) from the old draft id to the new real id whenever that
  // happens.
  //
  // sheetCardId must NOT be remapped for a plain (non-preset) destination:
  // handlePublished (in submitValues's onPublished callback) closes the
  // sheet by comparing sheetCardId against the exact id it was opened with
  // (the channel id) - for a multi-clip fanout, the FIRST clip's create can
  // land (and this effect fire) while later clips are still publishing,
  // remapping sheetCardId away from the channel id before handlePublished
  // ever runs. That match then fails and the sheet never closes, even
  // though handlePublished's own close logic is otherwise correct.
  useEffect(() => {
    const staleIds = new Set(
      [
        ...presetSheetGroupIds,
        ...(sheetCardId && presetSheetGroupIds.includes(sheetCardId)
          ? [sheetCardId]
          : []),
      ].filter((id) => !publishLookup.has(id))
    );
    if (staleIds.size === 0) return;
    const remap = new Map<string, string>();
    publishLookup.forEach((entry, realId) => {
      const channelId = (entry.data as any)?._channelId;
      if (channelId && staleIds.has(channelId)) {
        remap.set(channelId, realId);
      }
    });
    if (remap.size === 0) return;
    setPresetSheetGroupIds((prev) => prev.map((id) => remap.get(id) || id));
    setSheetCardId((prev) =>
      prev && presetSheetGroupIds.includes(prev)
        ? remap.get(prev) || prev
        : prev
    );
  }, [publishLookup, presetSheetGroupIds, sheetCardId]);

  const handleRemoveChip = useCallback(
    (entry: { type: "youtube" | "facebook" | "social"; data: any }) => {
      const { data, type } = entry;
      if (data.autoAdded) {
        setDismissedAutoIds((prev) => {
          const next = new Set(prev);
          next.add(data.id);
          return next;
        });
      }
      removePublish(data.id, type);
      setInvalidPublishIds((prev) => {
        if (!prev.has(data.id)) return prev;
        const next = new Set(prev);
        next.delete(data.id);
        return next;
      });
      // notifyDestinationRemoved below is the sole owner of sheetCardId's
      // fate here - it hops to a sibling tab if this was part of a preset
      // group, or closes the sheet otherwise. Closing it here first used to
      // race that hop within the same batch and could win depending on
      // React's update ordering, closing the whole sheet instead of hopping
      // to the next tab when the removed destination was the active one.
      setUnsavedTabIds((prev) => {
        if (!prev.has(data.id)) return prev;
        const next = new Set(prev);
        next.delete(data.id);
        return next;
      });
      notifyDestinationRemoved(data.id);
    },
    [removePublish, notifyDestinationRemoved]
  );

  // Unadd one destination from a preset's tab group in the sheet — thin
  // wrapper kept for call sites that don't need to pass tab-group bookkeeping
  // anymore (handleRemoveChip now owns that).
  const handleRemovePresetTab = handleRemoveChip;

  // Dismissing the sheet without saving (outside/backdrop click, or Escape)
  // fully removes every destination it was showing that isn't already saved
  // on the media (currentMediaDetails.socials) — same as the tab × — so the
  // list-side toggle turns back off instead of staying "on" for a
  // destination the user never actually finished/saved. A destination that's
  // already present in currentMediaDetails.socials must survive the
  // dismissal untouched — it only needs the sheet closed, not removed from
  // the page/chip list.
  const removeSheetDestinations = useCallback(() => {
    const tabIds = presetSheetGroupIds.filter((id) => publishLookup.has(id));
    const relevantIds =
      tabIds.length > 0 ? tabIds : sheetCardId ? [sheetCardId] : [];
    // A card's own id is now the record's own ES id (one card per record,
    // not per channel - see usePublishManagement), so "already saved" must
    // check against each record's own `id`, not `platform` (the channel id).
    const alreadySavedIds = new Set(
      (currentMediaDetails?.socials || []).map((s) => s.id)
    );
    const removableIds = relevantIds.filter((id) => !alreadySavedIds.has(id));
    removableIds.forEach((id) => {
      const entry = publishLookup.get(id);
      if (entry) handleRemoveChip(entry);
    });
    // The remaining (already-persisted) destinations survive the dismissal,
    // but any in-progress edit on them must be dropped, not kept around for
    // next time the card is opened - the user explicitly chose to close
    // without saving.
    const discardedEditIds = relevantIds.filter((id) =>
      alreadySavedIds.has(id)
    );
    if (discardedEditIds.length > 0) {
      const clearFn = (prev: any[]) =>
        prev.map((p) =>
          discardedEditIds.includes(p.id)
            ? {
                ...p,
                _editedFields: undefined,
                _editedValues: undefined,
                _dirty: undefined,
                _stagedPayload: undefined,
              }
            : p
        );
      setYoutubePublishes(clearFn);
      setFacebookPublishes(clearFn);
      setSocialPublishes(clearFn);
      setUnsavedTabIds((prev) => {
        if (discardedEditIds.every((id) => !prev.has(id))) return prev;
        const next = new Set(prev);
        discardedEditIds.forEach((id) => next.delete(id));
        return next;
      });
      // These ids survive the dismissal (already saved) rather than being
      // removed via handleRemoveChip (which collapses removed ids itself) -
      // without this, the card stays flagged as "expanded" after the sheet
      // closes, so the inline chip/list rendering behind the sheet shows the
      // full expanded card (video player, trim segment, etc.) instead of the
      // collapsed row.
    }
    setSheetCardId(null);
    setPresetSheetGroupIds([]);
    if (removableIds.length > 0) onSheetDismissed?.(removableIds);
  }, [
    presetSheetGroupIds,
    sheetCardId,
    publishLookup,
    handleRemoveChip,
    onSheetDismissed,
    currentMediaDetails?.socials,
    setYoutubePublishes,
    setFacebookPublishes,
    setSocialPublishes,
  ]);

  const expandSelected = useMemo(() => {
    const s = new Set<string>();
    publishOrder.forEach((id) => {
      const entry = publishLookup.get(id);
      if (!entry) return;
      const channelId = entry.data.id;
      const presetId = (entry.data as any)?._presetId as string | undefined;
      s.add(
        presetId ? presetItemId(presetId, channelId) : plainItemId(channelId)
      );
    });
    return s;
  }, [publishOrder, publishLookup]);

  const handleExpandApply = useCallback(
    (descriptors: ExpandDescriptor[]) => {
      const desiredChannelIds = new Set(descriptors.map((d) => d.channelId));

      descriptors.forEach((d) => {
        const existing = publishLookup.get(d.channelId);
        if (existing) {
          const existingPresetId = (existing.data as any)?._presetId as
            | string
            | undefined;
          const sameSource =
            d.kind === "preset"
              ? existingPresetId === d.presetId
              : !existingPresetId;
          if (sameSource) return; // already applied from this exact source
          if (!isOverridablePublish(existing.data)) return;
          removePublish(existing.data.id, existing.type);
        }

        const extra =
          d.kind === "preset"
            ? {
                _presetId: d.presetId,
                _presetName: d.presetName,
                _presetConfig: {
                  ...(d.config || {}),
                  isFromPreset: true,
                  job_type: d.channel.platform,
                  channel_name: d.channel.name,
                },
              }
            : {};

        const built = buildPublish(d.channel, extra);
        if (built) addPublish(built, d.channel.platform);
      });

      publishOrder.forEach((id) => {
        const entry = publishLookup.get(id);
        if (!entry) return;
        if (
          (entry.data as any)?.autoAdded &&
          String(entry.data.id).startsWith("auto-yt-")
        )
          return;
        if (desiredChannelIds.has(entry.data.id)) return;
        if (isDraftPublish(entry.data)) handleRemoveChip(entry);
      });
    },
    [
      publishLookup,
      publishOrder,
      buildPublish,
      addPublish,
      removePublish,
      handleRemoveChip,
    ]
  );

  const patchPublish = useCallback(
    (id: string, patch: Record<string, any>) => {
      const apply = (set: any) =>
        set((prev: any[]) =>
          prev.some((p) => p.id === id)
            ? prev.map((p) => (p.id === id ? ({ ...p, ...patch } as any) : p))
            : prev
        );
      apply(setYoutubePublishes);
      apply(setFacebookPublishes);
      apply(setSocialPublishes);
    },
    [setYoutubePublishes, setFacebookPublishes, setSocialPublishes]
  );

  // Records that the user manually edited a field on a card. Persisted on the
  // publish object (not card-local state) so the "don't re-sync this field" flag
  // survives the card unmounting when the sheet closes.
  const handleFieldEdited = useCallback(
    (id: string, field: "title" | "desc" | "tags" | "thumbnail") => {
      const setFn = (prev: any[]) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                _editedFields: { ...(p._editedFields || {}), [field]: true },
                // Mark dirty so the media-form Update flushes this saved
                // destination via updatePublishMedia (see publishStaged).
                _dirty: true,
              }
            : p
        );
      setYoutubePublishes(setFn);
      setFacebookPublishes(setFn);
      setSocialPublishes(setFn);
      setUnsavedTabIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    },
    [setYoutubePublishes, setFacebookPublishes, setSocialPublishes]
  );

  // Persists the current values of edited fields so the user's typed value
  // survives the card unmounting when the sheet closes.
  const handlePersistEditedValues = useCallback(
    (id: string, patch: Record<string, any>) => {
      const CONTENT_KEYS = ["title", "desc", "tags", "thumb", "thumb_id"];
      const contentPatch: Record<string, any> = {};
      const otherPatch: Record<string, any> = {};
      Object.keys(patch).forEach((k) => {
        if (CONTENT_KEYS.includes(k)) contentPatch[k] = patch[k];
        else otherPatch[k] = patch[k];
      });
      const touchesContent = Object.keys(contentPatch).length > 0;
      if (!touchesContent) {
        patchPublish(id, otherPatch);
        return;
      }
      const existing = publishLookup.get(id)?.data as any;
      const isPersisted = existing && existing.isNew !== 100;
      patchPublish(id, {
        ...otherPatch,
        // A persisted destination must keep showing its last-synced
        // currentMediaDetails.socials content on the collapsed card while
        // being live-edited - only _editedValues carries the typed-but-
        // unconfirmed value forward, restored onto the form when the sheet
        // reopens (see usePublishManagement's carryClientMarkers). A
        // brand-new destination has no backend record to preserve, so its
        // content IS whatever's being typed - show it directly.
        ...(isPersisted ? {} : contentPatch),
        _editedValues: { ...(existing?._editedValues || {}), ...contentPatch },
        _dirty: true,
      });
    },
    [patchPublish, publishLookup]
  );

  // Records that the user saved a card — from now on all its fields are frozen.
  const handleCardSaved = useCallback(
    (id: string) => {
      patchPublish(id, { _cardSaved: true });
      setUnsavedTabIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onDirty?.();
    },
    [patchPublish, onDirty]
  );

  const autoYtKey = autoYoutubeChannels
    .map(
      (c) =>
        `${c.channelId}:${(c.playlistIds || []).join(",")}:${c.category ?? ""}`
    )
    .join("|");
  useEffect(() => {
    if (!embedded) return;
    const channels = socialChannels?.socialChannelsList || [];
    const desired = autoYoutubeChannels.map(
      ({ channelId, channelName, playlistIds, category }) => {
        const match = channels.find(
          (c: SocialChannel) =>
            c.platform === "youtube" &&
            (c.channel === channelId || c.name === channelName)
        );
        return {
          chipId: match?.id || `auto-yt-${channelId}`,
          channelDetails:
            match || synthesizeYoutubeChannel(channelId, channelName),
          playlist: (playlistIds || []).filter(Boolean).join(","),
          category,
        };
      }
    );
    const desiredChipIds = new Set(desired.map((d) => d.chipId));

    desired.forEach(({ chipId, channelDetails, playlist, category }) => {
      if (dismissedAutoIds.has(chipId)) return;
      const existing = youtubePublishes.find((p) => p.id === chipId) as any;
      if (existing) {
        if (!isOverridablePublish(existing)) return;
        if (category && !existing.category) patchPublish(chipId, { category });
        return;
      }
      const built = buildPublish(channelDetails, {
        id: chipId,
        routing_id: chipId,
        autoAdded: true,
        ...(playlist ? { playlist } : {}),
        ...(category ? { category } : {}),
      });
      if (built) addPublish(built, "youtube");
    });

    youtubePublishes.forEach((p: any) => {
      if (
        p?.autoAdded &&
        String(p.id).startsWith("auto-yt-") &&
        !desiredChipIds.has(p.id)
      )
        removePublish(p.id, "youtube");
    });

    setDismissedAutoIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((id) => desiredChipIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    embedded,
    autoYtKey,
    socialChannels,
    youtubePublishes,
    dismissedAutoIds,
    buildPublish,
    addPublish,
    removePublish,
    patchPublish,
  ]);

  const ytSelectionKey = youtubePublishes
    .map((p: any) => p?.channel_name || "")
    .filter(Boolean)
    .join("|");
  useEffect(() => {
    if (!embedded) return;
    onYoutubeSelectionChange?.(ytSelectionKey ? ytSelectionKey.split("|") : []);
  }, [embedded, ytSelectionKey, onYoutubeSelectionChange]);

  const hasDraftDestination = useMemo(
    () =>
      publishOrder.some((id) => {
        const entry = publishLookup.get(id);
        return entry ? isDraftPublish(entry.data) : false;
      }),
    [publishOrder, publishLookup]
  );
  useEffect(() => {
    if (!embedded) return;
    if (hasDraftDestination) onDirty?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, hasDraftDestination]);

  const unstagedDraftEntries = useMemo(
    () =>
      publishOrder
        .map((id) => publishLookup.get(id))
        .filter(
          (entry): entry is NonNullable<typeof entry> =>
            !!entry &&
            isDraftPublish(entry.data) &&
            !(entry.data as any)?._stagedPayload
        ),
    [publishOrder, publishLookup]
  );

  const pendingPublishIds = useMemo(
    () =>
      publishOrder.filter((id) => {
        const entry = publishLookup.get(id);
        return entry ? isPendingPublish(entry.data) : false;
      }),
    [publishOrder, publishLookup]
  );
  const pendingRef = useRef<string[]>([]);
  pendingRef.current = pendingPublishIds;
  const publishLookupRef = useRef(publishLookup);
  publishLookupRef.current = publishLookup;
  const handleRemoveChipRef = useRef(handleRemoveChip);
  handleRemoveChipRef.current = handleRemoveChip;
  const presetSheetGroupIdsRef = useRef(presetSheetGroupIds);
  presetSheetGroupIdsRef.current = presetSheetGroupIds;

  const openSheet = useCallback(
    (id: string) => {
      setSheetCardId(id);
      if (!expandedCardIds.has(id)) handleCardInteraction(id);
      requestAnimationFrame(() => scrollToExistingPublish(id));
    },
    [expandedCardIds, handleCardInteraction, scrollToExistingPublish]
  );
  const openSheetRef = useRef(openSheet);
  openSheetRef.current = openSheet;

  useEffect(() => {
    if (!presetPlatformRef) return;
    presetPlatformRef.current = {
      removeChannel: (channelId: string) => {
        const entry = publishLookupRef.current.get(channelId);
        if (entry) handleRemoveChipRef.current(entry);
      },
      openChannel: (channelId: string) => {
        // The preset's stored channel id (frozen at preset-save time) can
        // drift from the actual live publish id when a channel got
        // matched by name+job_type instead of a direct id match (see the
        // fallback chain in the "Auto-open card based on item prop"
        // effect below) - e.g. a reconnected/renamed social channel.
        // Fall back to whatever this preset's own sheet group actually
        // opened with, so the click still lands on the right sheet.
        if (publishLookupRef.current.has(channelId)) {
          openSheetRef.current(channelId);
          return;
        }
        const fallbackId = presetSheetGroupIdsRef.current.find((id) =>
          publishLookupRef.current.has(id)
        );
        if (fallbackId) {
          openSheetRef.current(fallbackId);
        }
      },
    };
    return () => {
      presetPlatformRef.current = null;
    };
  }, [presetPlatformRef]);

  useEffect(() => {
    if (!socialGuardRef) return;
    socialGuardRef.current = {
      hasPendingPublish: () => pendingRef.current.length > 0,
      pendingCount: () => pendingRef.current.length,
      promptPublish: () => {
        const id = pendingRef.current[0];
        if (id) openSheet(id);
      },
      publishStaged: (opts) => publishStagedRef.current(opts),
      validateStaged: (opts) => validateStagedRef.current(opts),
    };
    return () => {
      socialGuardRef.current = null;
    };
  }, [socialGuardRef, openSheet]);

  const handlePublished = useCallback(
    (
      id: string,
      info: {
        auto?: boolean;
        status?: number;
        msg?: string;
        updated?: boolean;
        saved?: boolean;
      }
    ) => {
      const isDraftMedia = (currentMediaDetails as any)?.media_state === 0;
      if (info?.auto) {
        // Playlist-driven auto-add chip: never hits a real create/update RPC,
        // so there's no social.update socket event to wait for - this is the
        // only path where the card's own state is allowed to graduate itself
        // (in progress → completed) rather than syncing from
        // currentMediaDetails.socials.
        patchPublish(id, { isNew: 0, status: 1, autoAdded: false });
        setTimeout(() => patchPublish(id, { status: 10 }), 1800);
      }
      // For every real publish/save/update RPC, the card must never be kept
      // around locally from this response - it only ever exists/renders once
      // currentMediaDetails.socials actually has the record, which happens
      // when the social.update socket message arrives. Showing any local
      // card (draft or "submitted") here would be fabricated state that,
      // with the socket currently disabled, would never get corrected. The
      // one exception is a genuine failure: no socket event is ever coming
      // for a call that never created/updated anything, so there's nothing
      // to wait for - just toast it and leave the (still-local, unsubmitted)
      // card as-is so the user can fix and retry.
      const failed =
        !info?.auto && typeof info?.status === "number" && info.status < 0;
      if (failed) {
        return; // toast already raised by callPublishMedia; card stays as-is
      }
      // The non-embedded page never locally stages a draft (no onStageDraft)
      // - Save/Publish always calls the real create RPC directly. Once that
      // CREATE submit succeeds, drop the local draft entirely instead of
      // keeping it around under any flag - usePublishManagement's sync
      // effect will re-add this destination as soon as
      // currentMediaDetails.socials actually contains it.
      // An UPDATE (info.updated, or editing an existing record that was
      // never a local-only draft to begin with) must NOT be removed here -
      // it already exists in currentMediaDetails.socials and stays rendered
      // from that real record; removing it would just wipe the card off
      // screen until the next sync pass re-adds it, or longer if that sync
      // is delayed.
      // A destination that's part of a preset's tab group must also survive
      // here even as a fresh create - the tab stays open (see below), and
      // removing its entry from publishLookup while the sheet still points
      // at that id would orphan the sheet (blank body, no matching entry)
      // instead of showing anything.
      const isPresetTab = presetSheetGroupIdsRef.current.includes(id);
      if (!info?.auto && !info?.updated && !isPresetTab) {
        const entry = publishLookup.get(id);
        if (entry && isDraftPublish(entry.data)) {
          removePublish(id, entry.type);
        }
      }
      // A preset tab's fresh CREATE (not an edit-update of an already-
      // published record) leaves this same channel able to take another
      // "What to publish" mode (e.g. Clips after Full). The just-published
      // record lands separately via currentMediaDetails.socials under its
      // own id - channelHistory picks it up there regardless. This local
      // draft entry, still keyed by the stable channel id the tab points
      // at, gets reset back to a fresh isNew:100 draft (instead of staying
      // "submitted") so BasePublishCard remounts (key includes
      // _republishNonce) and its mode picker re-resolves to the next
      // available mode via resolveNextPublishMode/channelHistory, exactly
      // like reopening the tab from scratch already does.
      if (!info?.auto && !info?.updated && isPresetTab) {
        const entry = publishLookup.get(id);
        if (entry && isDraftPublish(entry.data)) {
          patchPublish(id, {
            isNew: 100,
            status: SOCIAL_PUBLISH_STATUS.QUEUE,
            _republishNonce: ((entry.data as any)?._republishNonce || 0) + 1,
            _editedFields: undefined,
            _editedValues: undefined,
            _dirty: undefined,
            // Marks this draft as a kept preset-tab re-attempt so
            // usePublishManagement's sync doesn't retire it once the
            // just-published record lands under its own id (see the
            // isKeptPresetDraft check there) - _presetId isn't set by this
            // toggle-based preset tab-group flow (only the separate "Reuse
            // Meta" expand flow sets that), so this explicit flag is the
            // actual signal.
            _isPresetTabDraft: true,
          });
          setUnsavedTabIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }
      setSheetOnlyPendingIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      // A preset tab stays open and selected after publishing - the mode
      // lock (channelHistory/publishRangeLocked in BasePublishCard) already
      // prevents re-publishing the same type on it, so there's no need to
      // remove or hop away from the tab; the user picks another platform's
      // tab, or another mode on this one, on their own.
      if (!isPresetTab) {
        setSheetCardId((prevSheetId) =>
          prevSheetId === id ? null : prevSheetId
        );
      }
      // A single card's Save/Publish always just closes the sheet - it must
      // not auto-hop to some other unrelated pending destination elsewhere
      // on the page (that coupling fired even for a plain individual-card
      // click, not just a genuine guided multi-destination sequence, which
      // this app has no explicit marker for today).
      if (info?.updated) {
        toast.success("Updated.");
      } else if (info?.saved) {
        toast.success("Saved.");
      } else {
        toast.success(
          isDraftMedia
            ? "Published — click Save & Publish to make the video active."
            : "Published."
        );
      }
    },
    [currentMediaDetails, patchPublish, publishLookup, removePublish]
  );

  const handleStageDraft = useCallback(
    (id: string, payload: any, opts?: { silent?: boolean }) => {
      const existing = publishLookup.get(id)?.data as any;
      const isPersisted = existing && existing.isNew !== 100;
      patchPublish(id, {
        // A persisted destination (already has a real record in
        // currentMediaDetails.socials) must keep showing that record's own
        // content on the collapsed card - not the just-typed, not-yet-sent
        // edit - until the real update lands and currentMediaDetails.socials
        // syncs via the social.update socket message. Only _stagedPayload
        // (below) carries the edit forward, for publishStaged() to flush
        // later; the visible fields are left untouched here.
        // A brand-new destination has no backend record at all yet, so its
        // only content IS the staged payload - show it.
        ...(isPersisted
          ? { _dirty: true }
          : {
              title: payload.title,
              desc: payload.desc,
              tags: payload.tags,
              thumb: payload.thumb,
              thumb_id: payload.thumb_id,
              privacy: payload.privacy,
              category: payload.category,
              playlist: payload.playlist,
              schedule: payload.schedule,
              monetize_policy_id: payload.monetize_policy_id,
              monetize_policy_name: payload.monetize_policy_name,
              is_created_for_kids: payload.is_created_for_kids,
              is_rights_managements_allowed:
                payload.is_rights_managements_allowed,
              isNew: 100,
              status: SOCIAL_PUBLISH_STATUS.QUEUE,
            }),
        // In-progress edit values, kept separate from the fields the
        // collapsed card renders (title/desc/tags/thumb above) so reopening
        // the sheet still shows what was typed, without that leaking onto
        // the collapsed card before the real update is confirmed.
        _editedValues: {
          ...(existing?._editedValues || {}),
          title: payload.title,
          desc: payload.desc,
          tags: payload.tags,
          thumb: payload.thumb,
          thumb_id: payload.thumb_id,
        },
        _affixApplied: true,
        _stagedPayload: payload,
      });

      setInvalidPublishIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      if (!opts?.silent) setSheetCardId(null);
    },
    [patchPublish, publishLookup]
  );

  const isChipActive = useCallback(
    (data: any, mediaWillBeActive: boolean): boolean => {
      if (!mediaWillBeActive) return false;
      // A chip the user saved as a draft (Save button, not Publish) must NOT be
      // validated/queued when the media is activated. Both the persisted flag and
      // the staged payload's status carry that intent — honor either.
      if ((data as any)?._markActive === false) return false;
      const stagedStatus = (data as any)?._stagedPayload?.status;
      if (stagedStatus === SOCIAL_PUBLISH_STATUS.DRAFT) return false;
      if ((data as any)?.status === SOCIAL_PUBLISH_STATUS.DRAFT) return false;
      return true;
    },
    []
  );

  const buildChipPayload = useCallback(
    (data: any) => {
      const payload = (data as any)?._stagedPayload
        ? { ...(data as any)._stagedPayload }
        : {
            ...buildDefaultPublishPayload(data),
            isSFTPEnabled:
              (data as any)?.isSFTPEnabled ?? (data as any)?.is_sftp,
          };

      const edited = (data as any)?._cardSaved
        ? { title: true, desc: true, tags: true, thumbnail: true }
        : (data as any)?._editedFields || {};
      if (edited.title && data?.title != null) payload.title = data.title;
      if (edited.desc && data?.desc != null) payload.desc = data.desc;
      if (edited.tags && data?.tags != null) payload.tags = data.tags;
      if (edited.thumbnail) {
        if (data?.thumb != null) payload.thumb = data.thumb;
        if (data?.thumb_id != null) payload.thumb_id = data.thumb_id;
      }
      const live = liveMediaRef.current;
      if (live) {
        const cardSaved = !!(data as any)?._cardSaved;
        const canSync = (field: string) => !cardSaved && !edited[field];
        if (canSync("title") && live.title != null) payload.title = live.title;
        if (canSync("desc") && live.desc != null) payload.desc = live.desc;
        if (canSync("tags") && live.tags != null) payload.tags = live.tags;
        if (canSync("thumbnail") && live.thumb != null) {
          payload.thumb = live.thumb;
          payload.thumb_id = live.thumb_id;
        }
      }
      const resolvedThumbId = resolveThumbIdByOrientation(currentMediaDetails);
      const thumbId =
        payload.thumb_id || (data as any)?.thumb_id || resolvedThumbId;
      if (!payload.thumb_id && thumbId) payload.thumb_id = thumbId;
      if (!payload.thumb) {
        const fallbackThumb =
          (data as any)?.thumb ||
          (thumbId
            ? getThumbnailUrlUtil({
                media: currentMediaDetails,
                id: thumbId,
                source: currentMediaDetails?.source,
              })
            : "");
        if (fallbackThumb) payload.thumb = fallbackThumb;
      }
      payload.edited = editedFieldsToArray(edited);
      // TEMP DEBUG
      console.log("[CAT_BUILDCHIP]", {
        id: data?.id,
        usedStaged: !!(data as any)?._stagedPayload,
        dataCategory: (data as any)?.category,
        stagedCategory: (data as any)?._stagedPayload?.category,
        payloadCategory: payload.category,
      });
      return payload;
    },
    [currentMediaDetails]
  );

  const isChipComplete = useCallback(
    (data: any): boolean => !validatePublishPayload(buildChipPayload(data)),
    [buildChipPayload]
  );

  const publishStaged = useCallback(
    (opts?: { mediaWillBeActive?: boolean }) => {
      if (pendingDeletesRef.current.length > 0) {
        pendingDeletesRef.current.forEach((d) => {
          if (d?.id) deleteSocialPublish({ id: d.id, type: d.type });
        });
        pendingDeletesRef.current = [];
      }

      const mediaWillBeActive =
        opts?.mediaWillBeActive ??
        (currentMediaDetails as any)?.media_state === 1;
      const isInstagram = (data: any) =>
        (data?.job_type || data?.platform) === "instagram";

      const wantsDraft = (data: any) =>
        data?._markActive === false ||
        data?._stagedPayload?.status === SOCIAL_PUBLISH_STATUS.DRAFT ||
        data?.status === SOCIAL_PUBLISH_STATUS.DRAFT;
      const resolveStatus = (data: any) => {
        if (wantsDraft(data)) return SOCIAL_PUBLISH_STATUS.DRAFT;
        if (isInstagram(data)) {
          return mediaWillBeActive
            ? SOCIAL_PUBLISH_STATUS.QUEUE
            : SOCIAL_PUBLISH_STATUS.DRAFT;
        }
        return isChipComplete(data)
          ? SOCIAL_PUBLISH_STATUS.QUEUE
          : SOCIAL_PUBLISH_STATUS.DRAFT;
      };
      publishOrder.forEach((id) => {
        const entry = publishLookup.get(id);
        if (!entry) return;

        if (isDraftPublish(entry.data)) {
          const payload = buildChipPayload(entry.data);
          payload.status = resolveStatus(entry.data);
          callPublishMedia(payload, (resp: any) => {
            patchPublish(id, {
              isNew: resp?.isNew,
              status: resp?.status,
              msg: resp?.msg,
              _stagedPayload: undefined,
              _dirty: false,
            });
          });
          return;
        }
        if ((entry.data as any)?._dirty && isPreActivationPublish(entry.data)) {
          const payload = buildChipPayload(entry.data);
          payload.id = (entry.data as any)?.published_id ?? payload.id;
          payload.status = resolveStatus(entry.data);
          updatePublishMedia(payload, (resp: any) => {
            patchPublish(id, {
              status: resp?.status ?? payload.status,
              msg: resp?.msg,
              _dirty: false,
            });
          });
        }
      });
    },
    [
      publishOrder,
      publishLookup,
      callPublishMedia,
      updatePublishMedia,
      patchPublish,
      deleteSocialPublish,
      currentMediaDetails,
      isChipComplete,
      buildChipPayload,
    ]
  );
  const publishStagedRef = useRef(publishStaged);
  publishStagedRef.current = publishStaged;

  const validateStaged = useCallback(
    (opts?: { mediaWillBeActive?: boolean }): string | null => {
      const invalid = new Set<string>();
      let firstError: string | null = null;
      const mediaWillBeActive =
        opts?.mediaWillBeActive ??
        (currentMediaDetails as any)?.media_state === 1;
      if (!mediaWillBeActive) {
        setInvalidPublishIds(invalid);
        return null;
      }
      for (const id of publishOrder) {
        const entry = publishLookup.get(id);
        if (!entry || !isPreActivationPublish(entry.data)) continue;
        if (!isChipActive(entry.data, mediaWillBeActive)) continue;
        const error = validatePublishPayload(buildChipPayload(entry.data));
        if (error) {
          invalid.add(id);
          if (!firstError) firstError = error;
        }
      }
      setInvalidPublishIds(invalid);
      return firstError;
    },
    [
      publishOrder,
      publishLookup,
      currentMediaDetails,
      isChipActive,
      buildChipPayload,
    ]
  );
  const validateStagedRef = useRef(validateStaged);
  validateStagedRef.current = validateStaged;

  // Memoize mediaBaseUrl to prevent recalculation on every render
  const mediaBaseUrl = useMemo(() => {
    const thumbBaseUrl = assets?.find((a) => a?.name === "thumb")?.baseurl;
    return (
      thumbBaseUrl ||
      assets?.[0]?.baseurl ||
      generateBaseUrl(currentMediaDetails) ||
      ""
    );
  }, [assets, currentMediaDetails]);

  // Memoize channel click handler to prevent unnecessary re-renders
  const handleChannelClick = useCallback(
    (channelId: string) => {
      handleChannelChange({ value: channelId, openSheetOnAdd: true });
      setActiveCardId(channelId);
    },
    [handleChannelChange, setActiveCardId]
  );

  // Helper function to render publish card with common props
  const renderPublishCard = (
    publish: YoutubePublish | FacebookPublish | SocialPublish,
    publishType: "youtube" | "facebook" | "social",
    mediaBaseUrl: string,
    autoStage = false,
    forceInSheet = false
  ) => {
    const commonCardProps = {
      initialdata: publish,
      assets,
      disabled:
        // Editable while new (100), in a draft/inactive state (status 0 or 12),
        // or failed (-1/-2) — failed needs to stay editable so Retry works.
        publish.isNew === 100 ||
        publish.status === SOCIAL_PUBLISH_STATUS.QUEUE ||
        publish.status === SOCIAL_PUBLISH_STATUS.DRAFT ||
        publish.status === SOCIAL_PUBLISH_STATUS.FAILED ||
        publish.status === -2
          ? false
          : true,
      handleCancelOrDelete,
      callPublishMedia,
      updatePublishMedia,
      isPublishingMedia,
      mediaBaseUrl,
      expandedId: expandedCardIds.has(publish.id) ? publish.id : null,
      onToggleExpand: () => handleCardInteraction(publish.id),
      updateModal,
      isWizard,
      currentMediaDetails,
      isPreset,
      handleAllSelectedPresets,
      item,
      presetDetails: (publish as any)?._presetConfig
        ? [(publish as any)._presetConfig]
        : presetDetails,
      isPreview,
      readOnlyMode,
      metaPresetData,
      inSheet: embedded || forceInSheet,
      onPublished: handlePublished,
      onStageDraft: embedded ? handleStageDraft : undefined,
      autoStage: embedded ? autoStage : false,
      onFieldEdited: handleFieldEdited,
      onPersistEditedValues: handlePersistEditedValues,
      onCardSaved: handleCardSaved,
    };

    if (publishType === "youtube") {
      return (
        <YoutubePublishCard
          {...commonCardProps}
          getYoutubePlaylist={getYoutubePlaylist}
          youtubePlaylist={youtubePlaylist}
          bunchLanguageList={bunchLanguageList}
          socialChannelList={
            socialChannels?.socialChannelsList as GenericSocialPublish[]
          }
        />
      );
    }

    // Facebook and other social platforms use SocialPublishCard
    return (
      <SocialPublishCard
        {...commonCardProps}
        socialChannelList={
          socialChannels?.socialChannelsList as unknown as SocialChannel[]
        }
      />
    );
  };

  // "Add new platform" UI — channel select + Recently Used chips (+ the
  // expand/picker sheet in embedded mode). Rendered as-is for embedded mode;
  // for the non-embedded page it's repositioned below the destination cards
  // (already-published cards first, then this).
  const addPlatformSection = (
    <>
      {!isPreview && (
        <div
          className={embedded ? "" : "socialFix position-sticky z-2"}
          style={
            embedded
              ? undefined
              : {
                  top: isWizard
                    ? "-14px"
                    : isPreset || readOnlyMode
                      ? "74px"
                      : "40px",
                  paddingTop: "12px",
                  background: "#fff",
                }
          }
        >
          {!embedded && <label>Social Publish</label>}
          <div className="position-relative">
            <CustomSelect
              name="selectedChannels"
              value={selectedChannel}
              placeholder={
                <div className="d-flex align-items-center gap-2">
                  <span>Choose an option to publish</span>
                </div>
              }
              handleChange={(newValue: any) =>
                handleChannelChange(
                  // Always open the destination in the side sheet (same as a
                  // "Recently Used" chip or an already-added destination) —
                  // without this, picking a brand-new destination from this
                  // dropdown fell through to handleChannelChange's
                  // inline-card branch instead.
                  { ...newValue, openSheetOnAdd: true }
                )
              }
              options={availableChannelOptions}
              hostSubSection={true}
              disabled={currentMediaDetails.source === "embed"}
              updatedOptions={availableChannelOptions}
              customOptionRenderer={(option) => option.customLabel}
              hideArrow={true}
              className="mb-2"
            />
            {/* Expand — browse destinations & presets in a side sheet (mirrors playlist picker) */}
            {embedded && currentMediaDetails.source !== "embed" && (
              <button
                type="button"
                className={`btn p-0 position-absolute d-inline-flex align-items-center justify-content-center border-0 ${
                  expandBtnHovered ? "text-dark bg-primary" : "text-secondary"
                }`}
                style={{
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                }}
                onMouseEnter={() => setExpandBtnHovered(true)}
                onMouseLeave={() => setExpandBtnHovered(false)}
                title="Browse destinations"
                aria-label="Browse destinations and presets"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandOpen(true);
                }}
              >
                <i
                  className="bi bi-arrows-angle-expand"
                  style={{ fontSize: 13 }}
                />
              </button>
            )}
          </div>
        </div>
      )}
      {embedded && (
        <SocialExpandSheet
          show={expandOpen}
          selected={expandSelected}
          onClose={() => setExpandOpen(false)}
          onApply={handleExpandApply}
        />
      )}
      {!isPreview && !embedded && (
        <>
          <label className="mt-2">Recently Used</label>
          <div
            className="d-flex flex-wrap gap-2 my-2 pb-3 border-bottom"
            style={
              currentMediaDetails.source === "embed"
                ? { pointerEvents: "none", opacity: 0.5, cursor: "not-allowed" }
                : { pointerEvents: "auto", opacity: 1, cursor: "pointer" }
            }
          >
            {mfuChannelOptions.map((channel) => (
              <div
                key={String(channel.value)}
                className="social-chip"
                onClick={() => handleChannelClick(String(channel.value))}
                style={{ cursor: "pointer" }}
              >
                {channel.customLabel}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );

  return (
    <div className={embedded ? "p-0" : "px-3 m-0"}>
      {addPlatformSection}
      {embedded ? (
        <>
          <div
            aria-hidden
            style={{
              position: "absolute",
              width: 0,
              height: 0,
              overflow: "hidden",
              opacity: 0,
              pointerEvents: "none",
            }}
          >
            {unstagedDraftEntries.map((entry) => (
              <div
                key={`stage:${entry.data.id}:${
                  (entry.data as any)?._presetId || "plain"
                }`}
              >
                {renderPublishCard(entry.data, entry.type, mediaBaseUrl, true)}
              </div>
            ))}
          </div>
          {publishOrder.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {publishOrder.map((publishId) => {
                const entry = publishLookup.get(publishId);
                if (!entry) return null;
                const data: any = entry.data;
                const isDraft = isDraftPublish(data);
                const isInvalid = invalidPublishIds.has(publishId);
                const openChipSheet = () => {
                  setSheetCardId(publishId);
                  if (!expandedCardIds.has(publishId))
                    handleCardInteraction(publishId);
                };
                return (
                  <div
                    key={publishId}
                    className="social-chip gap-1 p-1"
                    role="button"
                    tabIndex={0}
                    aria-label={`${data.channel_name} — open publish settings${
                      isInvalid ? " (needs attention)" : ""
                    }`}
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      width: "100%",
                      minHeight: "34px",
                      backgroundColor: isInvalid
                        ? "rgba(220, 53, 69, 0.2)"
                        : undefined,
                    }}
                    onClick={openChipSheet}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openChipSheet();
                      }
                    }}
                  >
                    {(() => {
                      const { icon, color } = getPlatformIcon(
                        data.job_type || data.platform || ""
                      );
                      return typeof icon === "string" ? (
                        <i
                          className={`bi ${icon}`}
                          style={{
                            color,
                            fontSize: 14,
                            flexShrink: 0,
                            paddingLeft: "5px",
                          }}
                        />
                      ) : (
                        <span style={{ display: "inline-flex", flexShrink: 0 }}>
                          {icon}
                        </span>
                      );
                    })()}
                    <span
                      className="text-truncate flex-grow-1 f-13"
                      style={{ maxWidth: 200 }}
                    >
                      {data.channel_name}
                    </span>
                    {isDraft ? (
                      <i
                        className="bi bi-x fs-4"
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${data.channel_name}`}
                        style={{ cursor: "pointer" }}
                        title="Remove destination"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveChip(entry);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveChip(entry);
                          }
                        }}
                      />
                    ) : (
                      <Tooltip content={statusTooltip(data)}>
                        <span
                          className={`py-1 px-2 badge fw-normal ${
                            socialPublishStatusToTextAndColor(data.status)
                              .statusColor
                          }`}
                          style={{ flexShrink: 0 }}
                        >
                          {
                            socialPublishStatusToTextAndColor(data.status)
                              .statusText
                          }
                        </span>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-muted small mt-2">
              {currentMediaDetails.source === "embed"
                ? "Embedded videos are not valid for social publishing."
                : "No social destination yet — add one above."}
            </div>
          )}
        </>
      ) : publishOrder.length > 0 || selectedChannel ? (
        <>
          {(() => {
            const publishedIds: string[] = [];
            const draftIds: string[] = [];
            publishOrder.forEach((publishId) => {
              const entry = publishLookup.get(publishId);
              if (!entry) return;
              // A destination added via a "Recently Used" chip lives only in
              // the sheet until actually published/saved — no page card yet.
              if (sheetOnlyPendingIds.has(publishId)) return;
              // The embedded page's local staging (handleStageDraft) keeps an
              // unsubmitted destination around under _affixApplied until the
              // media form's own Save & Update flushes it - that one still
              // collapses to the compact pencil-edit card rather than the
              // full inline form. The non-embedded page never reaches this
              // branch for a submitted destination at all anymore: once its
              // Save/Publish RPC completes, handlePublished removes it from
              // local state outright, and it only reappears once
              // currentMediaDetails.socials actually has the record.
              const isUnstagedDraft =
                isDraftPublish(entry.data) &&
                !(entry.data as any)?._affixApplied;
              (isUnstagedDraft ? draftIds : publishedIds).push(publishId);
            });

            // Already-submitted destinations (published/queued/failed/etc.) collapse
            // into a compact clickable card that opens the same sheet used from the
            // MediaDetail chip flow, instead of duplicating the full inline form here.
            // Fresh/draft destinations keep the existing full inline accordion card.
            // The two groups render in separate containers, published first.
            const renderPublishedCard = (publishId: string) => {
              const publishEntry = publishLookup.get(publishId);
              if (!publishEntry) return null;
              const { type: cardType, data: publish } = publishEntry;
              if (isPreview && publish.isNew !== 100) return null;

              const setDivRef = (el: HTMLDivElement | null) => {
                setCardRef(publish.id, el);
              };
              const openPublishSheet = () => {
                setSheetCardId(publish.id);
                markCardExpanded(publish.id);
              };
              const platformKey =
                (publish as any).job_type || (publish as any).platform || "";
              const { icon, color } = getPlatformIcon(platformKey);
              const isYoutubeCard = platformKey === "youtube";
              const matchedChannel = (
                socialChannels?.socialChannelsList as
                  | SocialChannel[]
                  | undefined
              )?.find((channel) => channel.id === (publish as any).channel_id);
              const channelAvatarSrc =
                platformKey === "facebook" && (publish as any).channel_id
                  ? `https://graph.facebook.com/${
                      (publish as any).channel_id
                    }/picture?type=large`
                  : matchedChannel?.image || noImage;
              const rawCategory = (publish as any).category;
              const categoryLabel = rawCategory
                ? DEFAULT_YOUTUBE_CATEGORIES[
                    rawCategory as keyof typeof DEFAULT_YOUTUBE_CATEGORIES
                  ] || rawCategory
                : null;
              const rawPrivacy = (publish as any).privacy;
              const privacyLabel = rawPrivacy
                ? getPlatformConfig(
                    platformKey || "youtube"
                  ).privacyOptions.find((o) => o.value === rawPrivacy)?.label ||
                  rawPrivacy
                : null;
              const rawPlaylist = (publish as any).playlist as
                | string
                | undefined;
              const playlistLabel = rawPlaylist
                ? rawPlaylist
                    .split(",")
                    .filter(Boolean)
                    .map(
                      (id) =>
                        youtubePlaylist?.find((p) => p.id === id)?.title || id
                    )
                    .join(", ")
                : null;
              const monetizeLabel =
                (publish as any).monetize_policy_name ||
                (publish as any).monetize_policy_id ||
                null;
              const YES_NO: Record<string, string> = { yes: "Yes", no: "No" };
              // Match BasePublishCard's own formik defaults for these two
              // fields ("no" for kids, "yes" for rights) — the raw publish
              // record from the socials list sync may not carry them at all,
              // so falling back to null (shown as "—") would disagree with
              // what the sheet itself displays for the same destination.
              const madeForKidsLabel =
                YES_NO[(publish as any).is_created_for_kids || "no"] || null;
              const rightsMgmtLabel =
                YES_NO[
                  (publish as any).is_rights_managements_allowed || "yes"
                ] || null;
              const title = (publish as any).title || "";
              const desc = (publish as any).desc || "";
              const tags: string[] = Array.isArray((publish as any).tags)
                ? (publish as any).tags
                : [];
              const { statusText, statusColor } =
                socialPublishStatusToTextAndColor(publish.status);
              const publishTypeLabel =
                {
                  [PUBLISH_RANGE_TYPE.CLIPS]: "Clip",
                  [PUBLISH_RANGE_TYPE.SEGMENT]: "Custom segment",
                }[(publish as any).publish_type as string] || "Full video";
              const isQueued = publish.status === SOCIAL_PUBLISH_STATUS.QUEUE;
              const isCompleted =
                publish.status === SOCIAL_PUBLISH_STATUS.COMPLETED;
              const footerText =
                (publish as any).schedule &&
                (publish as any).schedule >= Date.now()
                  ? `Scheduled for ${new Date(
                      (publish as any).schedule
                    ).toLocaleString()}`
                  : publish.status === SOCIAL_PUBLISH_STATUS.FAILED
                    ? "Failed"
                    : `${isCompleted ? "Published" : "Updated"} ${formatTimestampToAgo(
                        (publish as any).ct || Date.now()
                      )} · ${publish.channel_name}`;

              return (
                <div
                  key={publish.id}
                  ref={setDivRef}
                  className={`mt-2 socialCard rounded overflow-hidden border position-relative ${
                    activeCardId === publish.id ? "socialActive" : ""
                  }`}
                  style={{ scrollMarginTop: "100px" }}
                >
                  <div
                    className="px-3 py-2 pointer"
                    role="button"
                    tabIndex={0}
                    onClick={openPublishSheet}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPublishSheet();
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center gap-2 min-width-0">
                        <div
                          className="position-relative flex-shrink-0"
                          style={{ width: 35, height: 35 }}
                        >
                          <img
                            src={channelAvatarSrc}
                            alt={publish.channel_name}
                            width={35}
                            height={35}
                            className="rounded-circle bg-light"
                            style={{ objectFit: "cover" }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = noImage;
                            }}
                          />
                          {typeof icon === "string" ? (
                            <i
                              className={`bi ${icon} position-absolute d-flex align-items-center justify-content-center`}
                              style={{
                                color,
                                fontSize: 12,
                                bottom: -2,
                                right: -2,
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                lineHeight: 1,
                                width: "18px",
                                height: "18px",
                              }}
                            />
                          ) : (
                            <span
                              className="position-absolute d-inline-flex"
                              style={{
                                bottom: -2,
                                right: -2,
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                padding: 1,
                              }}
                            >
                              {icon}
                            </span>
                          )}
                        </div>
                        {isCompleted && (publish as any).routing_id ? (
                          <Tooltip content="View post">
                            <span
                              className="fw-medium f-14 text-truncate pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                openVideoInNewTab(
                                  platformKey,
                                  (publish as any).routing_id,
                                  publish.channel_name
                                );
                              }}
                            >
                              {publish.channel_name}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="fw-medium f-14 text-truncate">
                            {publish.channel_name}
                          </span>
                        )}
                        {isCompleted && (publish as any).routing_id && (
                          <Tooltip content="View post">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary p-0 flex-shrink-0 publishTypeEditBtn"
                              style={{ width: 24, height: 24 }}
                              aria-label="View post"
                              onClick={(e) => {
                                e.stopPropagation();
                                openVideoInNewTab(
                                  platformKey,
                                  (publish as any).routing_id,
                                  publish.channel_name
                                );
                              }}
                            >
                              <ViewIcon
                                width={12}
                                height={12}
                                fill="currentColor"
                              />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <Tooltip content={statusTooltip(publish)}>
                          <span
                            className={`badge fw-normal text-uppercase position-absolute bottom-0 end-0 ${statusColor}`}
                            style={{
                              borderRadius: "10px 0 0 0",
                              fontSize: "9px",
                            }}
                          >
                            {statusText}
                          </span>
                        </Tooltip>
                        <div
                          className="publishTypeSlot"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="badge fw-normal bg-light text-dark border text-truncate publishTypeBadge">
                            {publishTypeLabel}
                          </span>
                          <Tooltip content="Edit">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary p-0 publishTypeEditBtn"
                              style={{ width: 25, height: 25 }}
                              aria-label="Edit"
                              onClick={openPublishSheet}
                            >
                              <EditIcon
                                width={16}
                                height={16}
                                fill="currentColor"
                              />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    {title && (
                      <div
                        className="text-muted fw-medium f-13 mb-1 text-truncate"
                        title={title}
                      >
                        {title}
                      </div>
                    )}
                    {desc && (
                      <div
                        className="text-muted f-12"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: "18px",
                          height: 36,
                        }}
                        title={desc}
                      >
                        {desc}
                      </div>
                    )}
                    <hr className="my-2" />
                    <div className="d-flex flex-wrap row-gap-3">
                      {categoryLabel && (
                        <div className="col-md-4 text-truncate">
                          <label className="f-10 text-uppercase">
                            Category
                          </label>
                          <div
                            className="f-11 text-truncate"
                            title={categoryLabel}
                          >
                            {categoryLabel}
                          </div>
                        </div>
                      )}
                      {privacyLabel && (
                        <div className="col-md-4 text-truncate">
                          <label className="f-10 text-uppercase">
                            Visibility
                          </label>
                          <div
                            className="f-11 text-capitalize text-truncate"
                            title={privacyLabel}
                          >
                            {privacyLabel}
                          </div>
                        </div>
                      )}
                      {isYoutubeCard && (
                        <div className="col-md-4 text-truncate">
                          <label className="f-10 text-uppercase">
                            Playlist
                          </label>
                          <div
                            className="f-11 text-truncate"
                            title={playlistLabel || "—"}
                          >
                            {playlistLabel || "—"}
                          </div>
                        </div>
                      )}
                      {isYoutubeCard && (
                        <div className="col-md-4 text-truncate">
                          <label className="f-10 text-uppercase">
                            Monetize
                          </label>
                          <div
                            className="f-11 text-truncate"
                            title={monetizeLabel || "—"}
                          >
                            {monetizeLabel || "—"}
                          </div>
                        </div>
                      )}
                      {isYoutubeCard && (
                        <div className="col-md-4 text-truncate">
                          <label className="f-10 text-uppercase">
                            Made for Kids
                          </label>
                          <div className="f-11">{madeForKidsLabel || "—"}</div>
                        </div>
                      )}
                      {isYoutubeCard && (
                        <div className="col-md-4 text-truncate">
                          <label className="f-10 text-uppercase">
                            Rights Mgmt
                          </label>
                          <div className="f-11">{rightsMgmtLabel || "—"}</div>
                        </div>
                      )}
                    </div>
                    {tags.length > 0 && (
                      <div className="mt-2">
                        <label className="f-10 text-uppercase mb-1">Tags</label>
                        <div
                          className="d-flex flex-wrap gap-1"
                          style={{
                            height: 58,
                            overflow: "hidden",
                            alignContent: "flex-start",
                          }}
                        >
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              className="badge fw-normal bg-light text-dark border"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top f-13 text-muted H">
                    <span>{footerText}</span>
                    {isQueued && (
                      <div
                        className="d-flex gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          type="danger"
                          HTMLButtonType="button"
                          onClick={() =>
                            handleCancelOrDelete(publish, "delete", cardType)
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            };

            const renderDraftCard = (publishId: string) => {
              const publishEntry = publishLookup.get(publishId);
              if (!publishEntry) return null;
              const { type, data: publish } = publishEntry;
              if (isPreview && publish.isNew !== 100) return null;

              const setDivRef = (el: HTMLDivElement | null) => {
                setCardRef(publish.id, el);
              };
              return (
                <div
                  key={publish.id}
                  ref={setDivRef}
                  className={`mt-3 ${
                    activeCardId === publish.id ? "socialActive" : ""
                  }`}
                  style={{ scrollMarginTop: "100px" }}
                >
                  {renderPublishCard(publish, type, mediaBaseUrl)}
                </div>
              );
            };

            return (
              <>
                {publishedIds.length > 0 && (
                  <div
                    className="social-published-group d-grid gap-4"
                    style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
                  >
                    {publishedIds.map(renderPublishedCard)}
                  </div>
                )}
                {/* {draftIds.length > 0 && (
                  <div className="social-draft-group">
                    {draftIds.map(renderDraftCard)}
                  </div>
                )} */}
              </>
            );
          })()}
        </>
      ) : currentMediaDetails.source === "embed" ? (
        <div
          className="mt-3 d-flex flex-column align-items-center justify-content-center gap-2 rounded p-2"
          style={{ height: "150px" }}
        >
          {/* <i className="bi bi-exclamation-circle-fill fs-1"></i> */}
          <p className="text-center">
            Embedded videos are not valid for social publishing.
          </p>
        </div>
      ) : (
        <div
          className="mt-3 d-flex flex-column align-items-center justify-content-center gap-2 border rounded p-2 H"
          style={{ height: "150px" }}
        >
          <i className="bi bi-info-circle fs-2" style={{ cursor: "auto" }}></i>
          <p className="text-center">No Destination Selected</p>
        </div>
      )}
      <Offcanvas
        show={!!sheetCardId}
        // Our own confirm dialogs (AlertDialog/Modal) portal to document.body
        // outside this Offcanvas's DOM and manage their own focus/backdrop.
        // react-bootstrap's default enforceFocus doesn't know about them, so
        // it fights to yank focus back into the sheet on every focusin -
        // closing native <select> dropdowns and blocking typing in those
        // dialogs' inputs (e.g. the delete-confirm's reason select/captcha).
        enforceFocus={false}
        onHide={() => {
          const tabIds = presetSheetGroupIds.filter((id) =>
            publishLookup.has(id)
          );
          const relevantIds =
            tabIds.length > 0 ? tabIds : sheetCardId ? [sheetCardId] : [];
          const hasUnsaved = relevantIds.some((id) => unsavedTabIds.has(id));
          if (hasUnsaved) {
            setSheetCloseConfirmOpen(true);
          } else {
            removeSheetDestinations();
          }
        }}
        placement="end"
        backdrop
        style={{ width: "min(880px, 100vw)" }}
      >
        {(() => {
          const entry = sheetCardId ? publishLookup.get(sheetCardId) : null;
          const tabIds = presetSheetGroupIds.filter((id) =>
            publishLookup.has(id)
          );
          return (
            <>
              {tabIds.length > 1 && (
                <div className="d-flex gap-2 px-3 pt-3 border-bottom flex-wrap">
                  {tabIds.map((id) => {
                    const tabEntry = publishLookup.get(id);
                    if (!tabEntry) return null;
                    const { icon, color } = getPlatformIcon(
                      (tabEntry.data as any).job_type ||
                        (tabEntry.data as any).platform ||
                        ""
                    );
                    const isActive = id === sheetCardId;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`btn btn-sm btn-outline-primary d-flex align-items-center gap-1 position-relative ${
                          isActive ? "active" : ""
                        }`}
                        onClick={() => setSheetCardId(id)}
                        style={{
                          borderBottom: "0",
                          borderRadius: "0.3rem 0.3rem 0 0",
                        }}
                      >
                        {unsavedTabIds.has(id) && (
                          <span
                            className="position-absolute rounded-circle bg-danger"
                            style={{ top: 2, right: 2, width: 8, height: 8 }}
                            aria-label={`${tabEntry.data.channel_name} has unsaved changes`}
                            title="Unsaved changes"
                          />
                        )}
                        {typeof icon === "string" ? (
                          <i
                            className={`bi ${icon}`}
                            style={{
                              color: isActive ? undefined : color,
                              fontSize: 14,
                            }}
                          />
                        ) : (
                          icon
                        )}
                        <span
                          className="text-truncate"
                          style={{ maxWidth: 120 }}
                        >
                          {tabEntry.data.channel_name}
                        </span>
                        <i
                          className="bi bi-x ms-1"
                          role="button"
                          tabIndex={0}
                          aria-label={`Remove ${tabEntry.data.channel_name}`}
                          style={{ fontSize: 18, opacity: isActive ? 1 : 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePresetTab(tabEntry);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemovePresetTab(tabEntry);
                            }
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
              <Offcanvas.Header style={{ paddingBottom: 0 }}>
                <Offcanvas.Title className="f-18 d-flex align-items-center gap-2 border-bottom w-100 pb-2">
                  {entry &&
                    (() => {
                      const sheetPlatformKey =
                        (entry.data as any).job_type ||
                        (entry.data as any).platform ||
                        "";
                      const { icon, color } = getPlatformIcon(sheetPlatformKey);
                      const sheetMatchedChannel = (
                        socialChannels?.socialChannelsList as
                          | SocialChannel[]
                          | undefined
                      )?.find(
                        (channel) =>
                          channel.id === (entry.data as any).channel_id
                      );
                      const sheetAvatarSrc =
                        sheetPlatformKey === "facebook" &&
                        (entry.data as any).channel_id
                          ? `https://graph.facebook.com/${
                              (entry.data as any).channel_id
                            }/picture?type=large`
                          : sheetMatchedChannel?.image || noImage;
                      return (
                        <div
                          className="position-relative flex-shrink-0"
                          style={{ width: 28, height: 28 }}
                        >
                          <img
                            src={sheetAvatarSrc}
                            alt={entry.data.channel_name}
                            width={28}
                            height={28}
                            className="rounded-circle bg-light"
                            style={{ objectFit: "cover" }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = noImage;
                            }}
                          />
                          {typeof icon === "string" ? (
                            <i
                              className={`bi ${icon} position-absolute`}
                              style={{
                                color,
                                fontSize: 12,
                                bottom: -2,
                                right: -2,
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                lineHeight: 1,
                              }}
                            />
                          ) : (
                            <span
                              className="position-absolute d-inline-flex"
                              style={{
                                bottom: -2,
                                right: -2,
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                padding: 1,
                              }}
                            >
                              {icon}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  {entry?.data.channel_name || "Social publish"}
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body
                className="socialSheet-offcanvas-body"
                style={{
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {entry ? (
                  <div
                    key={`${entry.data.id}:${
                      (entry.data as any)?._presetId || "plain"
                    }:${(entry.data as any)?._republishNonce || 0}`}
                    style={{
                      height: "100%",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {renderPublishCard(
                      entry.data,
                      entry.type,
                      mediaBaseUrl,
                      false,
                      true
                    )}
                  </div>
                ) : null}
              </Offcanvas.Body>
            </>
          );
        })()}
      </Offcanvas>
      <AlertDialog
        isOpen={sheetCloseConfirmOpen}
        onClose={() => setSheetCloseConfirmOpen(false)}
        onConfirm={removeSheetDestinations}
        title="Close without saving?"
        message={(() => {
          const tabIds = presetSheetGroupIds.filter((id) =>
            publishLookup.has(id)
          );
          const relevantIds =
            tabIds.length > 0 ? tabIds : sheetCardId ? [sheetCardId] : [];
          const unsavedNames = relevantIds
            .filter((id) => unsavedTabIds.has(id))
            .map((id) => publishLookup.get(id)?.data?.channel_name)
            .filter(Boolean);
          return unsavedNames.length > 0
            ? `You have unsaved and unpublished changes in "${unsavedNames.join(
                '", "'
              )}". Do you still want to close it?`
            : "Do you really want to close without publishing or saving your social platform?";
        })()}
        confirmText="Yes"
        cancelText="No"
        confirmButtonType="danger"
      />
      <AlertDialog
        isOpen={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        onConfirm={() => {
          if (cancelConfirm) {
            performCancel(cancelConfirm.initialdata, cancelConfirm.cardType);
          }
        }}
        title="Remove destination?"
        message={`Remove "${
          cancelConfirm?.initialdata?.channel_name || "this destination"
        }"? Your unsaved changes for it will be lost.`}
        confirmText="Remove"
        cancelText="Keep"
        confirmButtonType="danger"
      />
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetails: state.media.mediaDetails.media,
  assets: state.media.mediaAssets.assets,
  socialChannels: state.app.userSocialChannels,
  isPublishingMedia: state.media.mediaPublish.isPublishingMedia,
  mostFrequentSocial: state.users.mostFrequentSocial,
  youtubePlaylist: state.app.youtubePlaylist.data,
  mediaAssets: state.media.mediaAssets.assets,
  bunchLanguageList: state.constants.bunchLanguageList.list,
});

const mapDispatch = {
  callPublishMedia,
  updatePublishMedia,
  getMostFrequentNoIndex,
  getYoutubePlaylist,
  deleteSocialPublish,
  takedownSocialPublish,
  updateModal,
  addMediaAsset,
  getSocialChannels,
};

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & IProps;
export default connector(AllSocialPublish);
